import { useCallback, useEffect, useMemo, useState } from "react";
import { cn, Select, SelectItem } from "@nextui-org/react";
import { Check } from "lucide-react";

import { ZButton } from "@/shared/components/button";
import { ZInput } from "@/shared/components/input";
import {
  ZModal,
  ZModalBody,
  ZModalContent,
  ZModalFooter,
  ZModalHeader,
} from "@/shared/components/modal";
import VendorSearch from "@/shared/components/VendorSearchAutocomplete";
import { useLoggedInMember } from "@/modules/auth/states";
// import { getZaniaTeamMemberRoles } from "@/modules/team/states";
// import { getPublishedQuestionnaires, type Questionnaire } from "@/modules/settings/requests/questionnaire";
import { useGetControlSetsQuery } from "@/modules/agent/requests";
// import { EMAIL_REGEX } from "@/shared/constants";
import { startsWithSpecialCharacter } from "../util";
import type { TPRAFormData } from "./types";

interface TPRAFormModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (data: TPRAFormData) => void;
  initialData?: TPRAFormData | null;
}

interface VendorObject {
  name: string;
  domain?: string;
  logo_url?: string;
}

type ErrorMessages = {
  [key: string]: string | undefined;
};

export function TPRAFormModal({
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
  initialData,
}: TPRAFormModalProps) {
  const member = useLoggedInMember();
  // const memberId = member?.member_id || "";
  // const { isTeamOrgAdmin, isTeamAdmin } = useMemo(
  //   () => (member ? getZaniaTeamMemberRoles(memberId, member) : { isTeamOrgAdmin: false, isTeamAdmin: false }),
  //   [member, memberId]
  // );

  // Form state
  const [vendorName, setVendorName] = useState<string>("");
  const [vendorObject, setVendorObject] = useState<VendorObject | null>(null);
  const [productName, setProductName] = useState<string>("");
  const [selectedControlSets, setSelectedControlSets] = useState<string[]>([]);
  // const [requestVendor, setRequestVendor] = useState<boolean>(false);
  // const [vendorEmail, setVendorEmail] = useState<string>("");
  // const [contactEmail, setContactEmail] = useState<string>("");
  // const [questionnaireId, setQuestionnaireId] = useState<string>("");
  const [errors, setErrors] = useState<ErrorMessages>({});

  // Questionnaires
  // const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  // const [isLoadingQuestionnaires, setIsLoadingQuestionnaires] = useState(false);

  // Control sets
  const { data: controlSets, isLoading: isLoadingControlSets, isFetching: isFetchingControlSets } =
    useGetControlSetsQuery({ isEnabled: isOpen });

  // Control set options
  const controlSetOptions = useMemo(() => {
    if (!controlSets) return [];
    return controlSets
      .filter((set: any) => set.number_of_controls > 0)
      .map((controlSet: any) => ({
        label: controlSet.controls_set_name,
        value: controlSet.id,
        isDefault: controlSet.default_control_set,
        number_of_controls: controlSet.number_of_controls,
      }));
  }, [controlSets]);

  // Reset form when modal opens, or restore from initialData if available
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // Restore form data when navigating back
        setVendorName(initialData.vendorName);
        setVendorObject({
          name: initialData.vendorName,
          domain: initialData.vendorDomain,
          logo_url: initialData.vendorLogoUrl,
        });
        setProductName(initialData.productName || "");
        setSelectedControlSets(initialData.selectedControlSets);
        // setRequestVendor(initialData.requestVendor);
        // setVendorEmail(initialData.vendorEmail || "");
        // setContactEmail(initialData.contactEmail || member?.email_address || "");
        // setQuestionnaireId(initialData.questionnaireId || "");
      } else {
        // Fresh form - reset all fields
        setVendorName("");
        setVendorObject(null);
        setProductName("");
        setSelectedControlSets([]);
        // setRequestVendor(false);
        // setVendorEmail("");
        // setContactEmail(member?.email_address || "");
        // setQuestionnaireId("");
      }
      setErrors({});
    }
  }, [isOpen, initialData]);

  // Load questionnaires when requestVendor is enabled
  // useEffect(() => {
  //   if (requestVendor) {
  //     const loadQuestionnaires = async () => {
  //       setIsLoadingQuestionnaires(true);
  //       try {
  //         const response = await getPublishedQuestionnaires();
  //         if (response?.success && response.questionnaires) {
  //           setQuestionnaires(response.questionnaires);
  //         }
  //       } catch (error) {
  //         console.error("Failed to load questionnaires:", error);
  //       } finally {
  //         setIsLoadingQuestionnaires(false);
  //       }
  //     };
  //     loadQuestionnaires();
  //   }
  // }, [requestVendor]);

  // Set contact email when requestVendor is toggled
  // useEffect(() => {
  //   if (requestVendor && member?.email_address && !contactEmail) {
  //     setContactEmail(member.email_address);
  //   }
  // }, [requestVendor, member?.email_address, contactEmail]);

  // const isValidEmail = useCallback((email: string) => {
  //   return EMAIL_REGEX.test(email);
  // }, []);

  const handleVendorSelect = useCallback((vendor: VendorObject) => {
    setVendorObject(vendor);
    setVendorName(vendor.name);
    setErrors((prev) => ({ ...prev, vendorName: undefined }));
  }, []);

  const handleVendorInputChange = useCallback((value: string) => {
    setVendorName(value.trimStart());
    if (!value.trim()) {
      setVendorObject(null);
    }
    setErrors((prev) => ({ ...prev, vendorName: undefined }));
  }, []);

  const validateForm = useCallback((): boolean => {
    const newErrors: ErrorMessages = {};

    // Vendor name validation
    const vendorNameValue = vendorObject?.name || vendorName;
    if (!vendorNameValue.trim()) {
      newErrors.vendorName = "Third-Party is required";
    } else if (startsWithSpecialCharacter(vendorNameValue)) {
      newErrors.vendorName = "Cannot start with a special character";
    }

    // Product name validation (optional but validate if provided)
    if (productName && startsWithSpecialCharacter(productName)) {
      newErrors.productName = "Cannot start with a special character";
    }

    // Control set validation
    if (selectedControlSets.length === 0) {
      newErrors.controlSet = "Please select at least one control set";
    }

    // Vendor email validation (when requestVendor is true)
    // if (requestVendor) {
    //   if (!vendorEmail.trim()) {
    //     newErrors.vendorEmail = "Vendor email is required";
    //   } else if (!isValidEmail(vendorEmail)) {
    //     newErrors.vendorEmail = "Please enter a valid email address";
    //   }
    //
    //   if (!contactEmail.trim()) {
    //     newErrors.contactEmail = "Contact email is required";
    //   } else if (!isValidEmail(contactEmail)) {
    //     newErrors.contactEmail = "Please enter a valid email address";
    //   }
    //
    //   if (vendorEmail.trim() && contactEmail.trim() && vendorEmail.trim() === contactEmail.trim()) {
    //     newErrors.vendorEmail = "Vendor email cannot be the same as contact email";
    //   }
    //
    //   if (!questionnaireId) {
    //     newErrors.questionnaireId = "Please select a questionnaire";
    //   }
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [vendorObject, vendorName, productName, selectedControlSets]);

  const handleSubmit = useCallback(() => {
    if (!validateForm()) return;

    const formData: TPRAFormData = {
      vendorName: vendorObject?.name || vendorName.trim(),
      vendorDomain: vendorObject?.domain,
      vendorLogoUrl: vendorObject?.logo_url,
      productName: productName.trim(),
      selectedControlSets,
      contactEmail: member?.email_address || undefined,
      // vendorEmail: requestVendor ? vendorEmail.trim() : undefined,
      // questionnaireId: requestVendor ? questionnaireId : undefined,
    };

    onSubmit(formData);
  }, [validateForm, vendorObject, vendorName, productName, selectedControlSets, member?.email_address, onSubmit]);

  const isButtonDisabled = useMemo(() => {
    // Basic required fields
    const vendorNameValue = vendorObject?.name || vendorName;
    if (!vendorNameValue.trim()) return true;
    if (selectedControlSets.length === 0) return true;

    // Vendor email validation
    // if (requestVendor) {
    //   if (!vendorEmail.trim() || !isValidEmail(vendorEmail)) return true;
    //   if (!contactEmail.trim() || !isValidEmail(contactEmail)) return true;
    //   if (vendorEmail.trim() === contactEmail.trim()) return true;
    //   if (!questionnaireId) return true;
    // }

    // Has validation errors
    if (Object.values(errors).some((e) => e)) return true;

    return false;
  }, [vendorObject, vendorName, selectedControlSets, errors]);

  return (
    <ZModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open && !isSubmitting) {
          onClose();
        }
      }}
      aria-label="TPRA Assessment Dialog"
      className="max-h-[calc(100vh-96px)] overflow-hidden"
    >
      <ZModalContent className="max-w-lg !my-0">
        <ZModalHeader className="flex text-lg flex-col w-full items-center pb-4">
          <p>Conduct Third-Party Risk Assessment</p>
          <p className="text-sm text-center font-normal text-[#777780]">
            Zania will use AI to conduct comprehensive third-party risk assessments
          </p>
        </ZModalHeader>
        <ZModalBody className="pt-0 overflow-auto">
          <div className="grid py-0 gap-4">
            <div className="grid w-full items-center gap-4">
              {/* Third-Party (Vendor) Search */}
              <VendorSearch
                label="Third-Party"
                placeholder="E.g.: Google"
                value={vendorName}
                onSelectionChange={handleVendorSelect}
                onInputChange={handleVendorInputChange}
                errorMessage={errors.vendorName}
                isInvalid={!!errors.vendorName}
              />

              {/* Product Name (Optional) */}
              <ZInput
                label="Product Name (Optional)"
                placeholder="E.g.: Drive"
                type="text"
                value={productName}
                onChange={(e) => {
                  setProductName(e.target.value.trimStart());
                  setErrors((prev) => ({ ...prev, productName: undefined }));
                }}
                labelPlacement="outside"
                maxLength={25}
                errorMessage={errors.productName}
                isInvalid={!!errors.productName}
                variant="faded"
                className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                classNames={{
                  inputWrapper: cn(
                    "w-full shadow-none rounded-md border-1 border-gray-300",
                    !!errors.productName && "border-[#F31260]"
                  ),
                }}
              />

              {/* Controls Dropdown (Multi-select) */}
              <Select
                label="Controls"
                placeholder="Select a control set"
                labelPlacement="outside"
                selectionMode="multiple"
                selectedKeys={new Set(selectedControlSets)}
                isLoading={isLoadingControlSets || isFetchingControlSets}
                isInvalid={!!errors.controlSet}
                errorMessage={errors.controlSet}
                classNames={{
                  trigger: cn(
                    "w-full shadow-none rounded-md border-1 border-gray-300",
                    !!errors.controlSet && "border-[#F31260]"
                  ),
                  listboxWrapper: "custom-dropdown-scrollbar",
                }}
                onSelectionChange={(keys) => {
                  const selectedValues = Array.from(keys as Set<string>);
                  const hasCreateNew = selectedValues.includes("create_new_control_set");
                  const currentSelection = selectedControlSets;

                  if (hasCreateNew && !currentSelection.includes("create_new_control_set")) {
                    setSelectedControlSets(["create_new_control_set"]);
                  } else if (
                    currentSelection.includes("create_new_control_set") &&
                    hasCreateNew &&
                    selectedValues.length === 2
                  ) {
                    const newOption = selectedValues.find((val) => val !== "create_new_control_set");
                    if (newOption) {
                      setSelectedControlSets([newOption]);
                    }
                  } else if (hasCreateNew && selectedValues.length > 2) {
                    setSelectedControlSets(["create_new_control_set"]);
                  } else if (hasCreateNew && selectedValues.length === 1) {
                    setSelectedControlSets(["create_new_control_set"]);
                  } else {
                    setSelectedControlSets(selectedValues);
                  }
                  setErrors((prev) => ({ ...prev, controlSet: undefined }));
                }}
                renderValue={(items) => {
                  if (!items || items.length === 0) return null;

                  if (items.length === 1) {
                    const item = items[0];
                    const option = controlSetOptions.find((opt: any) => opt.value === item.key);
                    return <span>{option?.label || item.textValue}</span>;
                  }
                  const firstItem = items[0];
                  const firstOption = controlSetOptions.find((opt: any) => opt.value === firstItem.key);
                  return (
                    <span>
                      {firstOption?.label || firstItem.textValue}{" "}
                      <span className="text-[#006FEE]">+{items.length - 1} more</span>
                    </span>
                  );
                }}
              >
                {controlSetOptions.map((item: any) => (
                  <SelectItem key={item.value} value={item.value}>
                    <div className="flex flex-row items-center w-full">
                      <div className="control-checkbox-wrapper">
                        <div className="control-checkbox-box">
                          <Check className="control-checkbox-check" />
                        </div>
                      </div>
                      <span className="text-sm font-normal">{item.label}</span>
                      {item.number_of_controls && (
                        <span className="text-xs text-[#71717A] ml-2">
                          {item.number_of_controls} controls
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </Select>

              {/* Request Vendor Toggle — commented out, now handled in LaunchStep */}
              {/* <div>
                <div
                  className="flex items-center cursor-pointer"
                  onClick={() => {
                    const newValue = !requestVendor;
                    setRequestVendor(newValue);
                    if (newValue && member?.email_address) {
                      setContactEmail(member.email_address);
                    }
                  }}
                >
                  <Switch
                    size="sm"
                    color="primary"
                    isSelected={requestVendor}
                    onValueChange={(value) => {
                      setRequestVendor(value);
                      if (value && member?.email_address) {
                        setContactEmail(member.email_address);
                      }
                    }}
                  />
                  <div className="pl-1 font-medium text-sm">Request Third-Party to Submit Details</div>
                </div>
              </div> */}

              {/* Vendor Email (when requestVendor is true) — commented out, now handled in LaunchStep */}
              {/* {requestVendor && (
                <ZInput
                  label="Vendor Email"
                  placeholder="vendor@company.com"
                  type="email"
                  value={vendorEmail}
                  onChange={(e) => {
                    setVendorEmail(e.target.value.trimStart());
                    setErrors((prev) => ({ ...prev, vendorEmail: undefined }));
                  }}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value && !isValidEmail(value)) {
                      setErrors((prev) => ({
                        ...prev,
                        vendorEmail: "Please enter a valid email address",
                      }));
                    }
                  }}
                  labelPlacement="outside"
                  errorMessage={errors.vendorEmail}
                  isInvalid={!!errors.vendorEmail}
                  variant="faded"
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                  classNames={{
                    inputWrapper: cn(
                      "w-full shadow-none rounded-md border-1 border-gray-300",
                      !!errors.vendorEmail && "border-[#F31260]"
                    ),
                  }}
                />
              )} */}

              {/* Questionnaire Dropdown (when requestVendor is true) — commented out, now handled in LaunchStep */}
              {/* {requestVendor && (
                <Select
                  label="Questionnaire and Document Set"
                  placeholder="Select"
                  selectedKeys={questionnaireId ? [questionnaireId] : []}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as string;
                    setQuestionnaireId(selected || "");
                    setErrors((prev) => ({ ...prev, questionnaireId: undefined }));
                  }}
                  isLoading={isLoadingQuestionnaires}
                  isInvalid={!!errors.questionnaireId}
                  errorMessage={errors.questionnaireId}
                  labelPlacement="outside"
                  variant="faded"
                  classNames={{
                    trigger: cn(
                      "border-1 border-gray-300 rounded-md",
                      !!errors.questionnaireId && "border-[#F31260]"
                    ),
                  }}
                >
                  {questionnaires.length === 0 ? (
                    <SelectItem key="empty" value="" isDisabled>
                      <div className="whitespace-normal">
                        {isTeamOrgAdmin || isTeamAdmin
                          ? "No sets found. Please visit Settings > Questionnaire & Document Sets to create one."
                          : "No sets found. Please contact your organization admin."}
                      </div>
                    </SelectItem>
                  ) : (
                    questionnaires.map((q) => {
                      const isEmpty =
                        q.number_of_questions === 0 && (q.number_of_evidences === 0 || !q.number_of_evidences);

                      return (
                        <SelectItem
                          key={q.id}
                          value={q.id}
                          isDisabled={isEmpty}
                          description={
                            isEmpty ? "Unavailable because it contains no questions or documents." : undefined
                          }
                        >
                          {q.questionnaire_name}
                        </SelectItem>
                      );
                    })
                  )}
                </Select>
              )} */}

              {/* Contact Email (when requestVendor is true) — commented out, now handled in LaunchStep */}
              {/* {requestVendor && (
                <ZInput
                  label="Contact Email"
                  placeholder="your@email.com"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => {
                    setContactEmail(e.target.value.trimStart());
                    setErrors((prev) => ({ ...prev, contactEmail: undefined }));
                  }}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value && !isValidEmail(value)) {
                      setErrors((prev) => ({
                        ...prev,
                        contactEmail: "Please enter a valid email address",
                      }));
                    }
                  }}
                  labelPlacement="outside"
                  errorMessage={errors.contactEmail}
                  isInvalid={!!errors.contactEmail}
                  variant="faded"
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                  classNames={{
                    inputWrapper: cn(
                      "w-full shadow-none rounded-md border-1 border-gray-300",
                      !!errors.contactEmail && "border-[#F31260]"
                    ),
                  }}
                />
              )} */}
            </div>
          </div>
        </ZModalBody>
        <ZModalFooter>
          <ZButton
            color="primary"
            className="w-full"
            isDisabled={isButtonDisabled || isSubmitting}
            isLoading={isSubmitting}
            onPress={handleSubmit}
          >
            {isSubmitting ? "Checking..." : "Start Assessment"}
          </ZButton>
        </ZModalFooter>
      </ZModalContent>
    </ZModal>
  );
}
